import { ROUTES_CONSTANTS } from "./constants/routesString";

export type StylistThemeColors = {
  primary: string;
  secondary: string;
  tertiary: string;
  theme_name?: string;
};

export const DEFAULT_STYLIST_THEME: StylistThemeColors = {
  primary: "#dbedeb",
  secondary: "#0d5a61",
  tertiary: "#0d5a61",
  theme_name: "default",
};

const RESERVED_FIRST_SEGMENTS = new Set<string>([
  "",
  ROUTES_CONSTANTS.signin.replace(/^\//, ""),
  ROUTES_CONSTANTS.signup.replace(/^\//, ""),
  ROUTES_CONSTANTS.forgotpwd.replace(/^\//, ""),
  ROUTES_CONSTANTS.resetpwd.replace(/^\//, ""),
  ROUTES_CONSTANTS.terms.replace(/^\//, ""),
  ROUTES_CONSTANTS.privacy.replace(/^\//, ""),
  ROUTES_CONSTANTS.dashboard.replace(/^\//, ""),
  ROUTES_CONSTANTS.test.replace(/^\//, ""),
  ROUTES_CONSTANTS.bookings.replace(/^\//, ""),
  ROUTES_CONSTANTS.no_route.replace(/^\//, ""),
]);

/**
 * Read the stylist unique code from the first URL path segment when present.
 */
export function getStylistCodeFromPathname(pathname: string): string | null {
  const segment = pathname.split("/").filter(Boolean)[0];
  if (!segment || RESERVED_FIRST_SEGMENTS.has(segment)) {
    return null;
  }
  return segment;
}

/**
 * Normalize API theme payload into CSS-ready hex values with fallbacks.
 */
export function normalizeStylistThemeColors(
  raw: Partial<StylistThemeColors> | null | undefined
): StylistThemeColors {
  if (!raw) {
    return { ...DEFAULT_STYLIST_THEME };
  }

  return {
    primary: raw.primary || DEFAULT_STYLIST_THEME.primary,
    secondary: raw.secondary || DEFAULT_STYLIST_THEME.secondary,
    tertiary: raw.tertiary || DEFAULT_STYLIST_THEME.tertiary,
    theme_name: raw.theme_name || DEFAULT_STYLIST_THEME.theme_name,
  };
}

/**
 * Apply stylist theme colors as CSS custom properties on the document root.
 */
export function applyStylistThemeToDocument(theme: StylistThemeColors): void {
  const root = document.documentElement;
  root.style.setProperty("--stylist-primary", theme.primary);
  root.style.setProperty("--stylist-secondary", theme.secondary);
  root.style.setProperty("--stylist-tertiary", theme.tertiary);
  root.setAttribute("data-stylist-theme", theme.theme_name || "custom");
}

/**
 * Remove stylist theme overrides and restore default branding.
 */
export function clearStylistThemeFromDocument(): void {
  const root = document.documentElement;
  root.style.removeProperty("--stylist-primary");
  root.style.removeProperty("--stylist-secondary");
  root.style.removeProperty("--stylist-tertiary");
  root.removeAttribute("data-stylist-theme");
}
